import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  ZVendorApplication, 
  VendorApplication, 
  OfferingTypes,
  OfferingTypeLabels,
  SaleMethods,
  SaleMethodLabels,
  ProductionRanges,
  ProductionRangeLabels,
  TargetAgentTypes,
  TargetAgentTypeLabels,
  TracksOutcomesOptions,
  TracksOutcomesLabels,
  type OfferingType,
  type SaleMethod,
  type ProductionRange,
  type TargetAgentType,
  type TracksOutcomes,
} from '@/contracts/vendor-application';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { submitVendorApplication, uploadVendorAsset, saveDraft, getDraft, deleteDraft } from '@/data/vendorApplications';
import { useToast } from '@/hooks/use-toast';
import { Turnstile } from '@/components/ui/Turnstile';
import { 
  Loader2, 
  CheckCircle, 
  GraduationCap, 
  Laptop, 
  Wrench, 
  MessageCircle, 
  Megaphone, 
  Settings, 
  MoreHorizontal,
  ShoppingCart,
  Calendar,
  Shuffle,
  DollarSign,
  Users,
  Target,
  FileCheck,
  Layers,
  Building,
  Phone,
  ArrowRight,
  ArrowLeft,
  Save,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Step configuration matching canonical flow
const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: null },
  { id: 'offering', title: 'What Are You?', icon: Laptop, fields: ['offering_type'] },
  { id: 'sale_method', title: 'How Is It Sold?', icon: ShoppingCart, fields: ['sale_method'] },
  { id: 'fingerprint', title: 'Your Ideal Agent', icon: Users, fields: [] },
  { id: 'tracking', title: 'Proof & Tracking', icon: Target, fields: ['tracks_outcomes'] },
  { id: 'pricing', title: 'Pricing', icon: DollarSign, fields: ['public_price', 'circle_pro_price'] },
  { id: 'terms', title: 'Commercial Terms', icon: FileCheck, fields: ['commercial_terms_acknowledged'] },
  { id: 'categories', title: 'Categories', icon: Layers, fields: ['product_category'] },
  { id: 'listing', title: 'Listing Builder', icon: Building, fields: [] },
  { id: 'call', title: 'Partner Call', icon: Phone, fields: [] },
];

const SESSION_KEY = 'partnerApplicationSessionId';

function getOrCreateSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

// Offering type icons
const OfferingTypeIcons: Record<OfferingType, React.ElementType> = {
  course: GraduationCap,
  software: Laptop,
  service: Wrench,
  consulting: MessageCircle,
  marketing: Megaphone,
  operations: Settings,
  other: MoreHorizontal,
};

// Sale method icons
const SaleMethodIcons: Record<SaleMethod, React.ElementType> = {
  buy_direct: ShoppingCart,
  consultation: Calendar,
  hybrid: Shuffle,
};

interface PartnerApplicationFormProps {
  onSuccess: () => void;
}

export default function PartnerApplicationForm({ onSuccess }: PartnerApplicationFormProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [isRestoringDraft, setIsRestoringDraft] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(getOrCreateSessionId());
  const { toast } = useToast();

  const form = useForm<VendorApplication>({
    resolver: zodResolver(ZVendorApplication),
    defaultValues: {
      service_type: 'product',
      agreed_to_terms: false,
      consent_to_processing: false,
      prior_ltd_experience: false,
      key_features: [],
      integrations: [],
      screenshots: [],
      production_ranges: [],
      target_agent_types: [],
      secondary_categories: [],
      commercial_terms_acknowledged: false,
      partner_call_booked: false,
    },
  });

  // Restore draft on mount
  useEffect(() => {
    const restoreDraft = async () => {
      try {
        const draft = await getDraft(sessionIdRef.current);
        if (draft && draft.form_data) {
          Object.entries(draft.form_data).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              form.setValue(key as keyof VendorApplication, value as any);
            }
          });
          setCurrentStep(draft.current_step);
        }
      } catch (error) {
        console.error('Failed to restore draft:', error);
      } finally {
        setIsRestoringDraft(false);
      }
    };
    restoreDraft();
  }, [form]);

  const progress = (currentStep / (STEPS.length - 1)) * 100;

  const handleSaveDraft = () => {
    saveDraft(sessionIdRef.current, currentStep, form.getValues());
    toast({
      title: 'Progress Saved',
      description: 'You can continue where you left off anytime.',
    });
  };

  const onSubmit = async (data: VendorApplication) => {
    if (!turnstileToken) {
      toast({
        title: 'Verification Required',
        description: 'Please complete the security check before submitting.',
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const tempId = crypto.randomUUID();
      
      if (logoFile) {
        const logoUrl = await uploadVendorAsset(logoFile, tempId, 'logo');
        data.logo_url = logoUrl;
      }
      
      if (screenshotFiles.length > 0) {
        const screenshotUrls = await Promise.all(
          screenshotFiles.map(file => uploadVendorAsset(file, tempId, 'screenshot'))
        );
        data.screenshots = screenshotUrls;
      }
      
      const applicationId = await submitVendorApplication(data);
      
      await deleteDraft(sessionIdRef.current);
      localStorage.removeItem(SESSION_KEY);
      
      toast({
        title: 'Application Submitted!',
        description: 'We\'ll be in touch after your partner call.',
      });
      
      navigate(`/vendor/setup/${applicationId}`);
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    const nextStepIndex = Math.min(currentStep + 1, STEPS.length - 1);
    setCurrentStep(nextStepIndex);
    saveDraft(sessionIdRef.current, nextStepIndex, form.getValues());
  };

  const prevStep = () => {
    const prevStepIndex = Math.max(currentStep - 1, 0);
    setCurrentStep(prevStepIndex);
    saveDraft(sessionIdRef.current, prevStepIndex, form.getValues());
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      saveDraft(sessionIdRef.current, stepIndex, form.getValues());
    }
  };

  // Toggle array value helper
  const toggleArrayValue = <T extends string>(field: keyof VendorApplication, value: T) => {
    const current = (form.watch(field) as T[]) || [];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    form.setValue(field, newValue as any);
  };

  if (isRestoringDraft) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Welcome screen (Step 0)
  if (currentStep === 0) {
    return (
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/60 mx-auto flex items-center justify-center">
            <Building className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="text-2xl font-bold">Welcome to the future of professional growth.</h2>
        </div>
        
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <p className="text-muted-foreground leading-relaxed mb-4">
            Gone are the days of agents buying blind.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Circle exists to connect the right professionals with the right solutions—in real time, with real data, and real accountability.
          </p>
          <p className="text-sm text-muted-foreground/80">
            This application takes about 8-10 minutes. Your progress is automatically saved.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button size="lg" className="w-full" onClick={nextStep}>
            Start Application
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleSaveDraft} className="text-muted-foreground">
            <Save className="mr-2 h-4 w-4" />
            Save & finish later
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between gap-1 overflow-x-auto pb-2">
        {STEPS.slice(1).map((step, idx) => {
          const stepIndex = idx + 1;
          const isActive = currentStep === stepIndex;
          const isCompleted = currentStep > stepIndex;
          const Icon = step.icon;
          
          return (
            <button
              key={step.id}
              onClick={() => goToStep(stepIndex)}
              disabled={stepIndex > currentStep}
              className={cn(
                "flex flex-col items-center gap-1 min-w-[60px] transition-all",
                stepIndex > currentStep && "opacity-40 cursor-not-allowed",
                stepIndex < currentStep && "cursor-pointer hover:opacity-80"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                isActive && "bg-primary text-primary-foreground",
                isCompleted && "bg-primary/20 text-primary",
                !isActive && !isCompleted && "bg-muted text-muted-foreground"
              )}>
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4" />
                ) : Icon ? (
                  <Icon className="w-4 h-4" />
                ) : (
                  <span className="text-xs font-medium">{stepIndex}</span>
                )}
              </div>
              <span className={cn(
                "text-[10px] text-center whitespace-nowrap",
                isActive ? "text-primary font-medium" : "text-muted-foreground"
              )}>
                {step.title}
              </span>
            </button>
          );
        })}
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        
        {/* Step 1: What Are You? (Offering Type) */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <StepHeader
              icon={Laptop}
              title="What best describes what you're offering?"
              description="Choose one to start — you can add more later"
            />
            
            <div className="grid gap-3">
              {OfferingTypes.map((type) => {
                const Icon = OfferingTypeIcons[type];
                const isSelected = form.watch('offering_type') === type;
                
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => form.setValue('offering_type', type)}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left",
                      isSelected 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{OfferingTypeLabels[type]}</span>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
            
            <p className="text-sm text-muted-foreground">
              If you offer multiple things, we'll add them one at a time. This keeps your listing clean and converts better.
            </p>
          </div>
        )}

        {/* Step 2: How Is It Sold? */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <StepHeader
              icon={ShoppingCart}
              title="How do agents purchase this?"
              description="Select the primary way customers engage with your offering"
            />
            
            <div className="grid gap-3">
              {SaleMethods.map((method) => {
                const Icon = SaleMethodIcons[method];
                const isSelected = form.watch('sale_method') === method;
                
                return (
                  <button
                    key={method}
                    type="button"
                    onClick={() => form.setValue('sale_method', method)}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left",
                      isSelected 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{SaleMethodLabels[method]}</span>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
            
            <Card className="p-4 bg-muted/50 border-dashed">
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> Even "custom" services must define a base or entry price. 
                Listings with clear starting prices convert significantly better.
              </p>
            </Card>
          </div>
        )}

        {/* Step 3: Your Ideal Agent (Fingerprint) */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <StepHeader
              icon={Users}
              title="Who do you actually help?"
              description="This feeds Circle's matching + scoring logic"
            />
            
            <div className="space-y-4">
              <Label className="text-base font-medium">Production Range</Label>
              <div className="grid grid-cols-2 gap-2">
                {ProductionRanges.map((range) => {
                  const isSelected = (form.watch('production_ranges') || []).includes(range);
                  
                  return (
                    <button
                      key={range}
                      type="button"
                      onClick={() => toggleArrayValue('production_ranges', range)}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-sm",
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      {ProductionRangeLabels[range]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-medium">Who do you serve?</Label>
              <div className="grid grid-cols-3 gap-2">
                {TargetAgentTypes.map((type) => {
                  const isSelected = (form.watch('target_agent_types') || []).includes(type);
                  
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleArrayValue('target_agent_types', type)}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-sm",
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      {TargetAgentTypeLabels[type]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="primary_pain">Primary pain solved (max 300 chars)</Label>
              <Textarea
                id="primary_pain"
                {...form.register('primary_pain')}
                placeholder="What's the #1 problem you solve for agents?"
                maxLength={300}
                rows={2}
              />
              <p className="text-xs text-muted-foreground text-right">
                {(form.watch('primary_pain') || '').length}/300
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="best_customer_description">Describe your best customer</Label>
              <Textarea
                id="best_customer_description"
                {...form.register('best_customer_description')}
                placeholder="Describe the agents who get the best results with you. Be specific: How much business are they doing? What problem are they actively trying to solve? What usually makes them convert?"
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                This is not marketing copy. This feeds Circle's matching + scoring logic.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Proof & Tracking */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <StepHeader
              icon={Target}
              title="Do you currently track customer outcomes?"
              description="Circle prioritizes partners who can demonstrate real outcomes"
            />
            
            <div className="grid gap-3">
              {TracksOutcomesOptions.map((option) => {
                const isSelected = form.watch('tracks_outcomes') === option;
                
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => form.setValue('tracks_outcomes', option)}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left",
                      isSelected 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    <span className="font-medium">{TracksOutcomesLabels[option]}</span>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
            
            <Card className="p-4 bg-muted/50 border-dashed">
              <p className="text-sm text-muted-foreground">
                You don't need perfection—but you do need a willingness to track results. 
                Circle prioritizes partners who can demonstrate real outcomes over time.
              </p>
            </Card>
          </div>
        )}

        {/* Step 5: Pricing */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <StepHeader
              icon={DollarSign}
              title="Set your pricing"
              description="Clear pricing builds trust and drives conversions"
            />
            
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="public_price">Public Price ($) *</Label>
                <Input
                  id="public_price"
                  type="number"
                  step="0.01"
                  {...form.register('public_price', { valueAsNumber: true })}
                  placeholder="499"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="circle_pro_price">Circle Pro Price ($) *</Label>
                <Input
                  id="circle_pro_price"
                  type="number"
                  step="0.01"
                  {...form.register('circle_pro_price', { valueAsNumber: true })}
                  placeholder="399"
                />
                <p className="text-xs text-muted-foreground">
                  Circle Pro members see exclusive pricing in exchange for volume, visibility, and verified buyers.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_price_note">Price Note (optional)</Label>
                <Input
                  id="base_price_note"
                  {...form.register('base_price_note')}
                  placeholder='e.g., "Starting at" or "Per month"'
                />
              </div>
            </div>
            
            {/* Price Preview */}
            {(form.watch('public_price') || form.watch('circle_pro_price')) && (
              <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <p className="text-sm font-medium mb-3">Preview</p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Public Price</p>
                    <p className="text-lg font-bold">${form.watch('public_price') || '—'}</p>
                  </div>
                  <div className="h-8 w-px bg-border" />
                  <div>
                    <p className="text-xs text-primary font-medium">Circle Pro</p>
                    <p className="text-lg font-bold text-primary">${form.watch('circle_pro_price') || '—'}</p>
                  </div>
                  {form.watch('public_price') && form.watch('circle_pro_price') && (
                    <div className="ml-auto bg-primary/20 text-primary text-xs font-medium px-2 py-1 rounded">
                      Save {Math.round(((form.watch('public_price')! - form.watch('circle_pro_price')!) / form.watch('public_price')!) * 100)}%
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Step 6: Commercial Terms */}
        {currentStep === 6 && (
          <div className="space-y-6">
            <StepHeader
              icon={FileCheck}
              title="Commercial Terms"
              description="Transparency, not legalese"
            />
            
            <Card className="p-6 space-y-4">
              <h3 className="font-semibold">Circle Commercial Terms</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  Circle earns 20% of revenue generated through the platform
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  Applies to one-time and recurring transactions
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  No upfront listing fees
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  You retain full ownership of your business and IP
                </li>
              </ul>
              
              <Button variant="link" className="p-0 h-auto text-primary" asChild>
                <a href="/terms" target="_blank">
                  View Full Terms & Conditions
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            </Card>
            
            <div className="flex items-start gap-3 p-4 rounded-lg border bg-muted/30">
              <Checkbox
                id="commercial_terms"
                checked={form.watch('commercial_terms_acknowledged')}
                onCheckedChange={(checked) => form.setValue('commercial_terms_acknowledged', !!checked)}
              />
              <Label htmlFor="commercial_terms" className="text-sm leading-relaxed cursor-pointer">
                I understand and agree to Circle's commercial terms.
              </Label>
            </div>
          </div>
        )}

        {/* Step 7: Categories & Discovery */}
        {currentStep === 7 && (
          <div className="space-y-6">
            <StepHeader
              icon={Layers}
              title="Categories & Discovery"
              description="Help agents find you in search and filters"
            />
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="product_category">Primary Category *</Label>
                <Input
                  id="product_category"
                  {...form.register('product_category')}
                  placeholder="e.g., CRM, Marketing, Coaching"
                />
                {form.formState.errors.product_category && (
                  <p className="text-sm text-destructive">{form.formState.errors.product_category.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondary_categories">Secondary Categories (optional)</Label>
                <Textarea
                  id="secondary_categories"
                  placeholder="Enter additional categories, comma-separated"
                  onChange={(e) => {
                    const cats = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                    form.setValue('secondary_categories', cats);
                  }}
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  These help with search and "Best for ___" pages.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 8: Listing Builder */}
        {currentStep === 8 && (
          <div className="space-y-6">
            <StepHeader
              icon={Building}
              title="Build Your Listing"
              description="Tell agents what you do and why it matters"
            />
            
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="product_name">Product/Service Name *</Label>
                <Input
                  id="product_name"
                  {...form.register('product_name')}
                  placeholder="What do you call your offering?"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  {...form.register('company_name')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="what_this_is">What this is</Label>
                <Textarea
                  id="what_this_is"
                  {...form.register('what_this_is')}
                  placeholder="One sentence describing your offering"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="who_its_for">Who it's for</Label>
                <Textarea
                  id="who_its_for"
                  {...form.register('who_its_for')}
                  placeholder="Describe your ideal customer"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="problem_solved">What problem it solves *</Label>
                <Textarea
                  id="problem_solved"
                  {...form.register('problem_solved')}
                  placeholder="What pain point do you address?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="what_success_looks_like">What success looks like</Label>
                <Textarea
                  id="what_success_looks_like"
                  {...form.register('what_success_looks_like')}
                  placeholder="What results can customers expect?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="what_happens_after_purchase">What happens after purchase</Label>
                <Textarea
                  id="what_happens_after_purchase"
                  {...form.register('what_happens_after_purchase')}
                  placeholder="Describe the onboarding/delivery process"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo">Logo (optional)</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="screenshots">Screenshots (optional, max 5)</Label>
                <Input
                  id="screenshots"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setScreenshotFiles(Array.from(e.target.files || []).slice(0, 5))}
                />
              </div>
            </div>
            
            {/* Live Preview Card */}
            {form.watch('product_name') && (
              <Card className="p-4 bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    {logoFile ? (
                      <img src={URL.createObjectURL(logoFile)} alt="Logo" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Laptop className="w-6 h-6 text-primary/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{form.watch('product_name')}</h4>
                    <p className="text-sm text-muted-foreground truncate">{form.watch('what_this_is') || 'Your value proposition here...'}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-bold">${form.watch('circle_pro_price') || form.watch('public_price') || '—'}</span>
                      {form.watch('public_price') && form.watch('circle_pro_price') && form.watch('public_price') > form.watch('circle_pro_price')! && (
                        <span className="text-xs line-through text-muted-foreground">${form.watch('public_price')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Step 9: Partner Call (Gate) */}
        {currentStep === 9 && (
          <div className="space-y-6">
            <StepHeader
              icon={Phone}
              title="Partner Alignment Call"
              description="Before we publish your listing"
            />
            
            <Card className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                We do a short alignment call to:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Confirm fit
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Review pricing and positioning
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Answer your questions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Finalize launch
                </li>
              </ul>
            </Card>

            {/* Contact Details */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Your Name *</Label>
                <Input id="contact_name" {...form.register('contact_name')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Your Email *</Label>
                <Input id="contact_email" type="email" {...form.register('contact_email')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_title">Your Title *</Label>
                <Input id="contact_title" {...form.register('contact_title')} />
              </div>
            </div>

            {/* Additional Required Fields */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium">Additional Details</h4>
              
              <div className="space-y-2">
                <Label htmlFor="company_website">Company Website *</Label>
                <Input id="company_website" type="url" {...form.register('company_website')} placeholder="https://" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="product_website">Product Website *</Label>
                <Input id="product_website" type="url" {...form.register('product_website')} placeholder="https://" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="product_brief_description">Brief Description (20+ chars) *</Label>
                <Textarea id="product_brief_description" {...form.register('product_brief_description')} rows={2} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="product_detailed_description">Detailed Description (100+ chars) *</Label>
                <Textarea id="product_detailed_description" {...form.register('product_detailed_description')} rows={3} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_audience">Target Audience *</Label>
                <Input id="target_audience" {...form.register('target_audience')} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current_price">Current Price ($) *</Label>
                  <Input id="current_price" type="number" {...form.register('current_price', { valueAsNumber: true })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="proposed_deal_price">Deal Price ($) *</Label>
                  <Input id="proposed_deal_price" type="number" {...form.register('proposed_deal_price', { valueAsNumber: true })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricing_model">Pricing Model *</Label>
                <Input id="pricing_model" {...form.register('pricing_model')} placeholder="e.g., One-time, Monthly" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deal_includes">What's Included *</Label>
                <Textarea id="deal_includes" {...form.register('deal_includes')} rows={2} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="key_features">Key Features (min 3, comma-separated) *</Label>
                <Textarea
                  id="key_features"
                  placeholder="Feature 1, Feature 2, Feature 3..."
                  onChange={(e) => {
                    const features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                    form.setValue('key_features', features);
                  }}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_goal">Primary Partnership Goal *</Label>
                <Textarea id="primary_goal" {...form.register('primary_goal')} rows={2} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="terms_url">Terms URL *</Label>
                  <Input id="terms_url" type="url" {...form.register('terms_url')} placeholder="https://" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="privacy_url">Privacy URL *</Label>
                  <Input id="privacy_url" type="url" {...form.register('privacy_url')} placeholder="https://" />
                </div>
              </div>
            </div>
            
            {/* Legal checkboxes */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agreed_to_terms"
                  checked={form.watch('agreed_to_terms')}
                  onCheckedChange={(checked) => form.setValue('agreed_to_terms', !!checked)}
                />
                <Label htmlFor="agreed_to_terms" className="text-sm cursor-pointer">
                  I agree to the Circle Network Partner Terms
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent_to_processing"
                  checked={form.watch('consent_to_processing')}
                  onCheckedChange={(checked) => form.setValue('consent_to_processing', !!checked)}
                />
                <Label htmlFor="consent_to_processing" className="text-sm cursor-pointer">
                  I consent to data processing for this application
                </Label>
              </div>
            </div>

            <Turnstile onVerify={setTurnstileToken} />
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSaveDraft}
            className="text-muted-foreground"
          >
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button type="button" onClick={nextStep}>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button type="submit" disabled={isSubmitting || !turnstileToken}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

// Step Header Component
function StepHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-start gap-4 pb-4 border-b">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6 text-primary-foreground" />
      </div>
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
